import type {
  CanvasConnector,
  CanvasLabeledEdge,
  CanvasSection,
  CanvasShape,
  CanvasTextLabel,
  CanvasTextNote,
  CanvasWidget,
} from "@cohesive/api-entities";
import type {
  BlockLayer,
  Entity,
  LegacyBlockPresentation,
} from "@cohesive/ui-components";
import type { Edge, EdgeProps, Node, NodeProps } from "@xyflow/react";

export type {
  CanvasConnector,
  CanvasLabeledEdge,
  CanvasSection,
  CanvasShape,
  CanvasTextLabel,
  CanvasTextNote,
  CanvasWidget,
};

/** React Flow `NodeProps` for a canvas-native node type. */
export type CanvasNodeProps<
  Data extends Record<string, unknown>,
  Type extends string,
> = NodeProps<Node<Data, Type>>;

/** Loro `canvasShape` node `data` — fields may be partial while editing / legacy snapshots. */
export type CanvasShapeNodeData = Partial<CanvasShape>;

/** Loro `canvasText` node `data` — mirrors toolbox `CanvasTextLabel` (partial while editing). */
export type CanvasTextLabelNodeData = Partial<CanvasTextLabel>;

/** Loro `textNote` node `data`. */
export type CanvasTextNoteNodeData = Partial<CanvasTextNote>;

/** Loro `section` node `data` — toolbox `CanvasSection` (partial). */
export type CanvasSectionNodeData = Partial<CanvasSection>;

/** Loro `canvasConnector` node `data` — toolbox `CanvasConnector` (partial). */
export type ConnectorNodeData = Partial<CanvasConnector> & {
  /** @deprecated Legacy local endpoints; prefer `start` / `end`. */
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  /** @deprecated Buggy nested shape from an old `updateConnectorEndpoints`; prefer `start` / `end`. */
  endpoints?: { x1?: number; y1?: number; x2?: number; y2?: number };
  /** @deprecated Combined arrow preset; prefer `start_arrow` / `end_arrow`. */
  arrow_mode?: "none" | "end" | "both";
};

export type ConnectorLocalPoint = { x: number; y: number };

export type ConnectorLocalEndpoints = {
  start: ConnectorLocalPoint;
  end: ConnectorLocalPoint;
};

function readConnectorPoint(value: unknown): ConnectorLocalPoint | null {
  if (!value || typeof value !== "object") return null;
  const p = value as { x?: unknown; y?: unknown };
  if (
    typeof p.x === "number" &&
    typeof p.y === "number" &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y)
  ) {
    return { x: p.x, y: p.y };
  }
  return null;
}

function readLegacyConnectorPoint(
  data: Record<string, unknown>,
  xKey: "x1" | "x2",
  yKey: "y1" | "y2",
): ConnectorLocalPoint | null {
  const x = data[xKey];
  const y = data[yKey];
  if (
    typeof x === "number" &&
    typeof y === "number" &&
    Number.isFinite(x) &&
    Number.isFinite(y)
  ) {
    return { x, y };
  }
  return null;
}

/** Local endpoints inside the React Flow node box (not absolute canvas coords). */
export function resolveConnectorLocalEndpoints(
  data: ConnectorNodeData | Record<string, unknown> | undefined,
  width: number,
  _height: number,
): ConnectorLocalEndpoints {
  const d = (data ?? {}) as Record<string, unknown>;
  const nested = d.endpoints as Record<string, unknown> | undefined;

  const start =
    readConnectorPoint(d.start) ??
    readLegacyConnectorPoint(d, "x1", "y1") ??
    (nested ? readLegacyConnectorPoint(nested, "x1", "y1") : null);

  const end =
    readConnectorPoint(d.end) ??
    readLegacyConnectorPoint(d, "x2", "y2") ??
    (nested ? readLegacyConnectorPoint(nested, "x2", "y2") : null);

  return {
    start: start ?? { x: 0, y: 0 },
    end: end ?? { x: width, y: 0 },
  };
}

/** Persist `start` / `end` and drop legacy endpoint keys from node data. */
export function connectorDataWithLocalEndpoints(
  data: Record<string, unknown>,
  start: ConnectorLocalPoint,
  end: ConnectorLocalPoint,
): Record<string, unknown> {
  const {
    x1: _x1,
    y1: _y1,
    x2: _x2,
    y2: _y2,
    endpoints: _endpoints,
    start: _start,
    end: _end,
    ...rest
  } = data;
  return { ...rest, start, end };
}

/** Loro `entity` node `data`. */
export type EntityNodeData = {
  /**
   * The wrapped entity. Real Liveblocks nodes carry the full entity payload.
   * Upload ghosts and agent placement ghosts carry a *partial* entity whose
   * `type` drives the ghost skeleton (kind / aspect ratio) and whose
   * `data.title` is used as the skeleton label — keeping the ghost data
   * shape isomorphic with the real node so consumers can branch on the
   * same `entity.type`.
   */
  entity?: Entity;
  /**
   * True for Liveblocks-backed loading skeleton nodes written by the FE at
   * tool-call-args-delta time (agent path) or at drop time (upload /
   * workspace-copy paths). Cleared by the backend via ReplaceNodeDataOp on
   * success, or the node is removed entirely on failure.
   */
  loading?: boolean;
  /**
   * Set when URL ingestion fails. The node stays on canvas so the user can
   * see what went wrong, open the original URL, and delete if needed.
   * Mutually exclusive with `loading: true`.
   */
  error?: {
    title: string;
    message?: string;
    /** The original URL that failed to ingest, shown as a clickable link. */
    sourceUrl?: string;
  };
  /**
   * The node's synced copy of the file record's display model — written by
   * the backend at placement so the card routes synchronously from room
   * storage. Read ONLY through blockDisplayFromNodeData (ui-components).
   */
  display?: {
    mode?: "block" | "document";
    chromeless?: boolean;
    layer?: BlockLayer;
  };
  /**
   * Legacy block marker (its presence meant "block"), still present on
   * canvases and published snapshots from before `display` existed. Read
   * ONLY through blockDisplayFromNodeData, which maps it permanently.
   */
  presentation?: LegacyBlockPresentation;
};

/** Loro `widgetNode` node `data` — `CanvasWidget` allows preset-specific keys. */
export type WidgetNodeData = Partial<CanvasWidget>;

/** React Flow `labeled` edge `data`. */
export type LabeledEdgeData = Partial<CanvasLabeledEdge>;

export type CanvasShapeNodeProps = CanvasNodeProps<
  CanvasShapeNodeData,
  "canvasShape"
>;
export type CanvasTextLabelNodeProps = CanvasNodeProps<
  CanvasTextLabelNodeData,
  "canvasText"
>;
export type CanvasTextNoteNodeProps = CanvasNodeProps<
  CanvasTextNoteNodeData,
  "textNote"
>;
export type CanvasSectionNodeProps = CanvasNodeProps<
  CanvasSectionNodeData,
  "section"
>;
export type ConnectorNodeProps = CanvasNodeProps<
  ConnectorNodeData,
  "canvasConnector"
>;
export type EntityNodeProps = CanvasNodeProps<EntityNodeData, "entity">;
export type WidgetNodeProps = CanvasNodeProps<WidgetNodeData, "widgetNode">;

/** Loro / RF payload for `chatSession` nodes (includes optional `userId` for per-user visibility). */
export type CanvasChatSessionNodeData = {
  flowSessionId: string;
  status?: string;
  firstMessage?: string;
  userId?: string;
  /** Whether the quick chat panel is expanded. Persisted so the open/collapsed
   *  state survives reloads. Safe to store on shared canvas data because chat
   *  session nodes are per-user (gated by `userId`). */
  expanded?: boolean;
};

export type CanvasChatSessionNodeProps = CanvasNodeProps<
  CanvasChatSessionNodeData,
  "chatSession"
>;

export type LabeledRFEdge = Edge<LabeledEdgeData, "labeled">;
export type LabeledEdgeProps = EdgeProps<LabeledRFEdge>;

const CANVAS_STROKE_WIDTHS = [1, 2, 4] as const;

export type CanvasStrokeWidth = (typeof CANVAS_STROKE_WIDTHS)[number];

/** Coerce arbitrary persisted stroke width to the toolbox enum (1, 2, 4). */
export function normalizeCanvasStrokeWidth(value: unknown): CanvasStrokeWidth {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 2;
  if (n <= 1) return 1;
  if (n >= 4) return 4;
  return 2;
}

const TEXT_LABEL_FONT_FAMILIES: NonNullable<CanvasTextLabel["font_family"]>[] =
  [
    "Inter, sans-serif",
    "Georgia, serif",
    "'Courier New', monospace",
    "'Comic Sans MS', cursive",
  ];

const TEXT_LABEL_FONT_SIZES: NonNullable<CanvasTextLabel["font_size"]>[] = [
  "16px",
  "20px",
  "32px",
  "48px",
];

/** Map persisted / UI font family to a toolbox `CanvasTextLabel.font_family` value. */
export function normalizeTextLabelFontFamily(
  raw: string | undefined,
): NonNullable<CanvasTextLabel["font_family"]> {
  if (raw && (TEXT_LABEL_FONT_FAMILIES as string[]).includes(raw)) {
    return raw as NonNullable<CanvasTextLabel["font_family"]>;
  }
  if (raw?.includes("Georgia")) return "Georgia, serif";
  if (raw?.includes("Courier")) return "'Courier New', monospace";
  if (raw?.includes("Comic")) return "'Comic Sans MS', cursive";
  return "Inter, sans-serif";
}

/** Map persisted / UI font size to a toolbox `CanvasTextLabel.font_size` value. */
export function normalizeTextLabelFontSize(
  raw: string | undefined,
): NonNullable<CanvasTextLabel["font_size"]> {
  if (raw && (TEXT_LABEL_FONT_SIZES as string[]).includes(raw)) {
    return raw as NonNullable<CanvasTextLabel["font_size"]>;
  }
  const m = raw?.match(/^(\d+)px$/);
  if (m) {
    const px = parseInt(m[1], 10);
    if (px <= 18) return "16px";
    if (px <= 26) return "20px";
    if (px <= 40) return "32px";
    return "48px";
  }
  return "20px";
}
